import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import {
  updateLatestDeviceDataCache,
  updateLatestMovementDataCache,
  upsertPacketByPlate,
} from "./realtimeDataCache";

const isValidClientId = (clientId) =>
  Boolean(clientId && clientId !== "null" && String(clientId).length > 0);

/**
 * SignalR subscription hook.
 *
 * Lifecycle is split in three effects so that changing the selected vehicle never
 * recreates the connection (and therefore never re-issues `/publisher/negotiate`):
 *
 * 1. Connection lifecycle - depends only on stable context (`client_id` and whether
 *    any subscription context exists at all).
 * 2. Connection start / listener binding / health monitoring - depends on the
 *    connection instance only.
 * 3. Vehicle subscription - re-subscribes on the existing connection when the
 *    selection (device imei / deviceId / selected plate) changes.
 *
 * See bugfix.md 1.6 / 2.6 and design "SignalR Lifecycle" (step 4).
 */
export const useWebSocket = (
  client_id,
  group = "all",
  method = "sub_all",
  imei,
  searchBy = "source_id",
  deviceId,
  selectedVehiclePlate,
  otherEvents = false
) => {
  const [connection, setConnection] = useState(null);
  const [realTimedata, setRealTimeData] = useState({});
  const [latestDeviceData, setLatestDeviceData] = useState({});
  const [latestMovementData, setLatestMovementData] = useState({});
  const [clientId, setClientId] = useState(null);
  const [deviceImei, setDeviceImei] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");

  // Debug logger (enable by setting REACT_APP_WS_DEBUG=true)
  const DEBUG = process.env.REACT_APP_WS_DEBUG === "true";
  const debugLog = useCallback(
    (...args) => {
      if (DEBUG) console.log(...args);
    },
    [DEBUG]
  );

  // The connection must be reachable from effects that do not depend on it.
  const connectionRef = useRef(null);
  const lastDataReceivedRef = useRef(null);

  // Subscription/listener context is read through refs so the connection and
  // listener effects can stay stable while the selection changes.
  const subscriptionKey = JSON.stringify([
    client_id ?? null,
    group ?? null,
    deviceImei ?? [],
    deviceId ?? null,
    selectedVehiclePlate ?? null,
  ]);
  const contextRef = useRef({});
  contextRef.current = { client_id, group, method, searchBy, deviceImei, subscriptionKey };
  const subscribedKeyRef = useRef(null);

  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  // Set the device imei if it is provided
  useEffect(() => {
    if (imei) {
      setDeviceImei([imei]);
    }
  }, [imei]);

  // ----- Stable helpers ---------------------------------------------------
  const handlePacket = useCallback((allData) => {
    try {
      const { Data } = JSON.parse(allData);
      const { searchBy: currentSearchBy } = contextRef.current;

      setRealTimeData((cache) => upsertPacketByPlate(cache, Data, currentSearchBy));
      setLatestDeviceData((cache) => updateLatestDeviceDataCache(cache, Data, currentSearchBy));
      setLatestMovementData((cache) => updateLatestMovementDataCache(cache, Data, currentSearchBy));

      lastDataReceivedRef.current = Date.now();
      setConnectionStatus("connected");
    } catch (error) {
      console.error("Error parsing received data:", error, "Raw:", allData);
    }
  }, []);

  const registerListener = useCallback(
    (activeConnection) => {
      if (!activeConnection || typeof activeConnection.on !== "function") return;

      const { method: currentMethod, deviceImei: imeis, client_id: cid } = contextRef.current;

      // De-duplicate any existing handler first (reconnect safety, design 3.9)
      try {
        activeConnection.off(currentMethod);
      } catch (e) {
        /* ignore if not present */
      }

      if (imeis?.length || isValidClientId(cid)) {
        debugLog("[Listeners] Registering listener for:", currentMethod);
        activeConnection.on(currentMethod, handlePacket);
      } else {
        debugLog("[Listeners] No valid context to register listener (device/client)");
      }
    },
    [debugLog, handlePacket]
  );

  const unsubscribeByContext = useCallback(async (activeConnection) => {
    if (!activeConnection) return;
    const { deviceImei: imeis, client_id: cid, group: grp } = contextRef.current;

    try {
      if (imeis?.length) {
        await activeConnection.invoke("UnSubscribe", imeis, grp);
      } else if (isValidClientId(cid)) {
        await activeConnection.invoke("UnSubscribeByClientId", cid, grp);
      }
    } catch (e) {
      console.error("Unsubscribe error:", e);
    }
  }, []);

  const subscribeByContext = useCallback(
    async (activeConnection) => {
      if (!activeConnection) return;
      const { deviceImei: imeis, client_id: cid, group: grp } = contextRef.current;

      if (imeis?.length) {
        await activeConnection.invoke("Subscribe", imeis, grp);
      } else if (isValidClientId(cid)) {
        await activeConnection.invoke("SubscribeByClientId", cid, grp);
      } else {
        debugLog("[Subscribe] No valid device or client context; skipping subscribe");
      }
    },
    [debugLog]
  );

  const resubscribeAndRebind = useCallback(
    async (activeConnection, reason) => {
      debugLog(`[Resubscribe] reason: ${reason}`);
      await unsubscribeByContext(activeConnection);
      await subscribeByContext(activeConnection);
      registerListener(activeConnection);
      subscribedKeyRef.current = contextRef.current.subscriptionKey;
    },
    [debugLog, registerListener, subscribeByContext, unsubscribeByContext]
  );

  // ----- 1. Connection lifecycle -----------------------------------------
  // Depends only on stable context: the client and whether there is any
  // subscription context at all. Selecting another vehicle keeps both stable, so
  // the connection (and its `/publisher/negotiate` call) is never recreated.
  const hasSubscriptionContext = Boolean(deviceId || selectedVehiclePlate || otherEvents || imei);

  useEffect(() => {
    let cancelled = false;

    const setupConnection = async () => {
      const existingConnection = connectionRef.current;

      if (isValidClientId(client_id) && hasSubscriptionContext) {
        if (existingConnection) {
          debugLog("Connection already exists, keeping it (no re-negotiate).");
          return;
        }

        try {
          debugLog("Setting up new WebSocket connection...");

          const newConnection = new HubConnectionBuilder()
            .withUrl(process.env.REACT_APP_PUBLISHER_URL)
            .withAutomaticReconnect()
            .build();

          if (cancelled) {
            try {
              await newConnection.stop();
            } catch (e) {
              /* ignore */
            }
            return;
          }

          connectionRef.current = newConnection;
          setConnection(newConnection);
          setClientId(client_id);
          debugLog("WebSocket connection setup completed, state:", newConnection.state);
        } catch (error) {
          console.error("Error setting up WebSocket connection:", error);
        }
      } else if (existingConnection) {
        debugLog("No subscription context — stopping connection.");
        try {
          await existingConnection.stop();
        } catch (e) {
          console.error("Error stopping connection:", e);
        } finally {
          connectionRef.current = null;
          subscribedKeyRef.current = null;
          setConnection(null);
          setClientId(null);
          setConnectionStatus("disconnected");
        }
      }
    };

    setupConnection();

    return () => {
      cancelled = true;
    };
  }, [client_id, hasSubscriptionContext, debugLog]);

  // ----- 2. Connection start, listener binding and health monitoring -----
  useEffect(() => {
    if (!connection) return undefined;

    let isStarting = false;
    let isHealthy = false;
    let healthMonitoringSetup = false;
    let connectionCheckInterval;
    let heartbeatInterval;

    function setupConnectionHealthMonitoring() {
      if (connectionCheckInterval) clearInterval(connectionCheckInterval);
      if (heartbeatInterval) clearInterval(heartbeatInterval);

      debugLog("Setting up connection health monitoring...");

      // Check connection state every 30 seconds (less frequent to avoid loops)
      connectionCheckInterval = setInterval(() => {
        if (connection.state !== "Connected" && !isStarting && !isHealthy) {
          debugLog("Connection not connected, attempting restart. State:", connection.state);
          isHealthy = false;
          start();
        }
      }, 30000);

      // Simple connection health check every 60 seconds (no server method calls)
      heartbeatInterval = setInterval(() => {
        try {
          if (connection.state === "Connected" && isHealthy) {
            const lastDataReceived = lastDataReceivedRef.current;
            const timeSinceLastData = lastDataReceived ? Date.now() - lastDataReceived : null;

            // If no data for more than 2 minutes, attempt a silent re-subscribe
            if (timeSinceLastData !== null && timeSinceLastData > 120000) {
              (async () => {
                try {
                  await resubscribeAndRebind(connection, "inactivity");
                } catch (e) {
                  console.error("Error during inactivity re-subscription:", e);
                }
              })();
            }
          }
        } catch (error) {
          debugLog("Health check error:", error);
        }
      }, 60000);
    }

    async function start() {
      if (isStarting) {
        debugLog("Connection start already in progress, skipping...");
        return;
      }

      if (connection.state === "Connected") {
        isHealthy = true;
        setConnectionStatus("connected");
        return;
      }

      setConnectionStatus("connecting");

      try {
        isStarting = true;

        await connection.start();

        // Ensure we are subscribed to the correct context and the listener is bound
        await resubscribeAndRebind(connection, "initial start");
        connection.serverTimeoutInMilliseconds = 5 * 60 * 1000;

        debugLog("WebSocket connection started successfully");
        isStarting = false;
        isHealthy = true;
        setConnectionStatus("connected");

        if (!healthMonitoringSetup) {
          setupConnectionHealthMonitoring();
          healthMonitoringSetup = true;
        }
      } catch (e) {
        console.error("websocket error:", e);
        isStarting = false;
        isHealthy = false;
        setConnectionStatus("disconnected");

        // Only attempt to reconnect if the connection is actually dead
        if (connection.state === "Disconnected" || connection.state === "Reconnecting") {
          setTimeout(() => {
            if (!isStarting && !isHealthy) {
              debugLog("Attempting to reconnect after error...");
              start();
            }
          }, 5000);
        }
      }
    }

    connection.onclose(async (error) => {
      debugLog("WebSocket connection closed:", error);
      isStarting = false;
      isHealthy = false;
      setConnectionStatus("disconnected");

      setTimeout(() => {
        debugLog("Attempting to reconnect after connection close...");
        start();
      }, 3000);
    });

    connection.onreconnecting((error) => {
      debugLog("WebSocket reconnecting:", error);
      isHealthy = false;
      setConnectionStatus("reconnecting");
    });

    connection.onreconnected(() => {
      isStarting = false;
      isHealthy = true;
      setConnectionStatus("connected");

      // Re-subscribe after reconnect because hub group membership is lost on a new
      // connectionId; `registerListener` de-dups the handler so rows/listeners are
      // never duplicated (design 3.9).
      (async () => {
        try {
          await resubscribeAndRebind(connection, "onreconnected");
        } catch (e) {
          console.error("Error re-subscribing after reconnect:", e);
        }
      })();
    });

    if (connection.state !== "Connected") {
      debugLog("Calling start() function...");
      start();
    } else {
      isHealthy = true;
      registerListener(connection);

      if (!healthMonitoringSetup) {
        setupConnectionHealthMonitoring();
        healthMonitoringSetup = true;
      }
    }

    return () => {
      if (connectionCheckInterval) clearInterval(connectionCheckInterval);
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      try {
        connection.stop();
      } catch (e) {
        console.error("Error stopping connection during cleanup:", e);
      }
    };
  }, [connection, debugLog, registerListener, resubscribeAndRebind]);

  // ----- 3. Vehicle subscription ------------------------------------------
  // Re-subscribes on the existing connection when the selection changes. It never
  // stops or rebuilds the connection, so no additional negotiate happens.
  useEffect(() => {
    if (!connection) return;
    if (connectionStatus !== "connected" && connection.state !== "Connected") return;
    if (subscribedKeyRef.current === subscriptionKey) return;

    let cancelled = false;
    subscribedKeyRef.current = subscriptionKey;

    (async () => {
      try {
        await unsubscribeByContext(connection);
        if (cancelled) return;
        await subscribeByContext(connection);
      } catch (e) {
        console.error("Error updating subscription for selection change:", e);
      }
    })();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [
    connection,
    connectionStatus,
    subscriptionKey,
    subscribeByContext,
    unsubscribeByContext,
  ]);

  return { realTimedata, latestDeviceData, latestMovementData, connectionStatus };
};
